import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useAccount } from '@hcs/authn';
import {
  Button,
  Card,
  CloseButton,
  DeleteIcon,
  IconButton,
  Input,
  MailIcon,
  Skeleton,
  StatusBadge,
  TextButton,
} from '@hcs/design-system';
import { useScreenSize } from '@hcs/hooks';
import { AgentEmailResponse } from '@hcs/types';

import { useAgentEmails } from '../../hooks/useAgentEmails';
import { useCreateAgentEmail } from '../../hooks/useCreateAgentEmail';
import { useDeleteAgentEmail } from '../../hooks/useDeleteAgentEmail';
import { useResendAgentEmailConfirmation } from '../../hooks/useResendAgentEmailConfirmation';
import { useUserAgentMlsPartner } from '../../hooks/useUserAgentMlsPartner';

import styles from './ListingDashboardAgentEmails.module.css';

const dataHcName = 'listing-dashboard-agent-emails';

const yupSchema = yup.object().shape({
  email: yup.string().email('Enter a valid email address').required(),
});

interface EmailFormValues {
  email: string;
}

interface EmailRow {
  key: string;
  emailAddress: string;
  /** Static label shown beside the email, e.g. "Account email" */
  label?: string;
  confirmed: boolean;
  deletable: boolean;
  /** Present only for user-managed agent emails backed by the API */
  agentEmailId?: string;
}

export interface ListingDashboardAgentEmailsProps {
  /** URL embedded in the confirmation email the user receives */
  confirmUrl: string;
  /** Controls the top-right close button; the button is hidden when undefined */
  onClose?: () => void;
}

interface AgentEmailListRowProps {
  row: EmailRow;
  userId: number | undefined;
  confirmUrl: string;
}

const AgentEmailListRow = ({
  row,
  userId,
  confirmUrl,
}: AgentEmailListRowProps) => {
  const { mutate: resendConfirmation, isPending: isResending } =
    useResendAgentEmailConfirmation();
  const { mutate: deleteAgentEmail, isPending: isDeleting } =
    useDeleteAgentEmail();

  return (
    <div className={styles.Row} data-hc-name={`${dataHcName}-row`}>
      <div className={styles.RowLeft}>
        <MailIcon
          className={styles.MailIcon}
          size={20}
          dataHcName={`${dataHcName}-row-mail-icon`}
        />
        <span className={styles.Email}>{row.emailAddress}</span>
        {row.label && <span className={styles.LabelChip}>{row.label}</span>}
      </div>
      <div className={styles.RowRight}>
        <StatusBadge
          status={row.confirmed ? 'success' : 'created'}
          label={row.confirmed ? 'Confirmed' : 'Pending confirmation'}
          indicator
        />
        {!row.confirmed && row.agentEmailId && (
          <TextButton
            dataHcName={`${dataHcName}-resend`}
            disabled={isResending || userId === undefined}
            onClick={() => {
              if (userId === undefined || !row.agentEmailId) return;
              resendConfirmation({
                userId,
                agentEmailId: row.agentEmailId,
                payload: { confirmUrl },
              });
            }}
          >
            Resend link
          </TextButton>
        )}
        {row.deletable && row.agentEmailId && (
          <IconButton
            dataHcName={`${dataHcName}-delete`}
            ariaLabel={`Remove ${row.emailAddress}`}
            disabled={isDeleting || userId === undefined}
            onClick={() => {
              if (userId === undefined || !row.agentEmailId) return;
              deleteAgentEmail({ userId, agentEmailId: row.agentEmailId });
            }}
            icon={
              <DeleteIcon
                className={styles.DeleteIcon}
                size={20}
                dataHcName={`${dataHcName}-delete-icon`}
              />
            }
          />
        )}
      </div>
    </div>
  );
};

export const ListingDashboardAgentEmails = ({
  confirmUrl,
  onClose,
}: ListingDashboardAgentEmailsProps) => {
  const { data: account } = useAccount();
  const { data: agentMlsPartner } = useUserAgentMlsPartner();
  const { data: agentEmails, isLoading: isLoadingAgentEmails } =
    useAgentEmails();
  const { mutate: createAgentEmail, isPending: isCreating } =
    useCreateAgentEmail({
      onSuccess: () => {
        form.reset({ email: '' });
      },
    });
  const { isMobileWidth } = useScreenSize();

  const form = useForm<EmailFormValues>({
    resolver: yupResolver(yupSchema),
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: { email: '' },
  });

  const userId = account?.user.id;
  const accountEmail = account?.user.email;
  const mlsEmail = agentMlsPartner?.agentEmailAddress;

  // Emails that are represented by a fixed, non-deletable row and should not be
  // duplicated by the API-backed list.
  const reservedEmails = new Set(
    [accountEmail, mlsEmail]
      .filter((email): email is string => !!email)
      .map((email) => email.toLowerCase()),
  );

  const rows: EmailRow[] = [];
  if (accountEmail) {
    rows.push({
      key: 'account-email',
      emailAddress: accountEmail,
      label: 'Account email',
      confirmed: true,
      deletable: false,
    });
  }
  if (mlsEmail && mlsEmail.toLowerCase() !== accountEmail?.toLowerCase()) {
    rows.push({
      key: 'agent-verified-email',
      emailAddress: mlsEmail,
      label: 'Agent Verified Email',
      confirmed: true,
      deletable: false,
    });
  }
  (agentEmails || []).forEach((agentEmail: AgentEmailResponse) => {
    const emailAddress = agentEmail.email_address;
    if (!emailAddress || reservedEmails.has(emailAddress.toLowerCase())) {
      return;
    }
    rows.push({
      key: agentEmail.id || emailAddress,
      emailAddress,
      confirmed: !!agentEmail.confirmed,
      deletable: true,
      agentEmailId: agentEmail.id,
    });
  });

  const existingEmails = new Set(
    rows.map((row) => row.emailAddress.toLowerCase()),
  );

  const handleSubmit = form.handleSubmit((values) => {
    if (userId === undefined) return;
    if (existingEmails.has(values.email.toLowerCase())) {
      form.setError('email', {
        message: 'This email address is already on your list',
      });
      return;
    }
    createAgentEmail({
      userId,
      payload: { email_address: values.email, confirmUrl },
    });
  });

  return (
    <Card
      dataHcName={dataHcName}
      noPadding={isMobileWidth}
      noBoxShadow={isMobileWidth}
    >
      <div className={styles.Header}>
        <div className={styles.HeaderText}>
          <h2 className={styles.Title}>Not seeing all your listings?</h2>
          <p className={styles.Description}>
            Your listings may be tied to a different email address than the one
            you signed in with. Add it below and we&rsquo;ll send you an email
            to confirm that it&rsquo;s yours. Once confirmed, any listings
            associated with the email that are syndicated to Google will appear
            in the Listings Dashboard.
          </p>
        </div>
        {onClose && (
          <CloseButton
            dataHcName={`${dataHcName}-close`}
            className={styles.CloseButton}
            onClick={onClose}
          />
        )}
      </div>

      <div className={styles.List}>
        {rows.map((row) => (
          <AgentEmailListRow
            key={row.key}
            row={row}
            userId={userId}
            confirmUrl={confirmUrl}
          />
        ))}
        {isLoadingAgentEmails && (
          <div className={styles.RowSkeleton}>
            <Skeleton />
          </div>
        )}
      </div>

      <FormProvider {...form}>
        <form className={styles.Form} onSubmit={handleSubmit} noValidate>
          <div className={styles.FormField}>
            <label className={styles.FormLabel} htmlFor={`${dataHcName}-email`}>
              Email address
            </label>
            <Controller
              name="email"
              control={form.control}
              render={({ field }) => (
                <Input
                  {...field}
                  value={field.value || ''}
                  name={`${dataHcName}-email`}
                  dataHcName={`${dataHcName}-email`}
                  placeholder="name@example.com"
                  type="email"
                  error={form.formState.errors.email?.message}
                />
              )}
            />
          </div>
          <Button
            dataHcName={`${dataHcName}-submit`}
            type="submit"
            className={styles.SubmitButton}
            loading={isCreating}
            disabled={
              isCreating || !form.formState.isValid || userId === undefined
            }
          >
            Send confirmation link
          </Button>
        </form>
      </FormProvider>
    </Card>
  );
};
